import { SVGProps } from 'react';

/**
 * icons — shared monochrome LINE icons (Feather-style, stroke = currentColor).
 *
 * Populus rule (punchlist §4): NO emoji used as icons anywhere. These stroke
 * SVGs replace the emoji/glyph icons the demo carried (sign-out text, the 👥
 * followers chip, the 🗄 archive toggle, the ★/☆ follow star) so icons render
 * identically across platforms and inherit text colour + size.
 *
 * Each takes an optional `size` (px, default 16) and spreads the rest onto the
 * <svg>, so callers can pass `aria-label`, `style`, etc. Decorative by default
 * (aria-hidden); pass an explicit aria-label to make one meaningful.
 */

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number;
}

function svgBase(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
}

/** Log-out / exit door with arrow (Feather "log-out"). */
export function IconSignOut({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/** Two-person group (Feather "users"). */
export function IconUsers({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** Star (Feather "star"); `filled` fills it with currentColor (following state). */
export function IconStar({ size = 16, filled = false, ...rest }: IconProps & { filled?: boolean }) {
  return (
    <svg {...svgBase(size)} fill={filled ? 'currentColor' : 'none'} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

/** Archive box (Feather "archive"). */
export function IconArchive({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

// Line padlock — marks a locked/built-in value (replaces the 🔒 emoji, retest B4).
export function IconLock({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Pencil / edit (Feather "edit-2"). */
export function IconPencil({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

/** Bell — an event that notifies (Feather "bell"). */
export function IconBell({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

/** Struck-through bell — an event that stays silent (Feather "bell-off"). */
export function IconBellOff({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
      <path d="M18 8a6 6 0 0 0-9.33-5" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** Warning triangle — a caution the user must read (Feather "alert-triangle"). */
export function IconAlertTriangle({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svgBase(size)} aria-hidden={rest['aria-label'] ? undefined : true} {...rest}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
